import { deviceDetector } from '../utils/deviceDetector';
import store, { SEGMENT_HOST } from '../store';
import { MediaInformation } from '../types/chromecast-caf-receiver/cast.framework.messages';
import { OverlayManager } from './index';

/**
 * Generates markup for stats overlay.
 * @param {object} props Properties for the stats overlay.
 * @returns {string} Markup.
 */
type Props = {
  width?: number;
  height?: number;
  bufferingTime?: number;
  streamBandwidth?: number;
  estimatedBandwidth?: number;
  droppedFrames?: number;
  dynamicRange?: boolean;
};

function getDevice() {
  const castDeviceVersion = deviceDetector();

  const instance = (cast.framework as any).CastReceiverContext.getInstance();
  const capabilities = instance.getDeviceCapabilities();

  return `${castDeviceVersion} - ${
    capabilities.is_hdr_supported ? 'HDR supported' : 'HDR not supported'
  }`;
}

function getMediaInformation() {
  const instance = (cast.framework as any).CastReceiverContext.getInstance();
  const mediaInformation: MediaInformation = instance.getPlayerManager().getMediaInformation();

  return mediaInformation;
}

export const stats = (props: Props) => {
  const mediaInformation = getMediaInformation();
  const metadata = mediaInformation?.metadata ?? {};

  const dataRows = [
    {
      title: 'Device',
      shouldShow: true,
      data: getDevice(),
    },
    {
      title: 'User Agent',
      shouldShow: true,
      data: navigator.userAgent.toString(),
    },
    {
      title: 'Playing',
      shouldShow: Boolean(mediaInformation),
      data: `${mediaInformation.contentId}: ${'title' in metadata ? metadata.title : ''}`,
    },
    {
      title: 'Current segment host',
      shouldShow: Boolean(store.get(SEGMENT_HOST)),
      data: store.get(SEGMENT_HOST),
    },
    {
      title: 'Stream Resolution',
      shouldShow: Boolean(props.width && props.height),
      data: `${props.width}x${props.height} ${props.dynamicRange}`,
    },
    {
      title: 'Current Buffer Length',
      shouldShow: Boolean(props?.bufferingTime),
      data: `${props?.bufferingTime} secs`,
    },
    {
      title: 'Current Stream Bandwidth',
      shouldShow: Boolean(props?.streamBandwidth),
      data: `${((props?.streamBandwidth ?? 0) / 1000 / 1000).toFixed(2)}Mbit/s`,
    },
    {
      title: 'Your estimated bandwidth',
      shouldShow: Boolean(props?.estimatedBandwidth),
      data: `${((props?.estimatedBandwidth ?? 0) / 1000 / 1000).toFixed(2)}Mbit/s`,
    },

    {
      title: 'Dropped frames',
      shouldShow: Boolean(props?.droppedFrames),
      data: props?.droppedFrames,
    },
  ];

  const formatDataRow = (row: { title: string; shouldShow: boolean; data: string }) => {
    if (!row.shouldShow) return '';

    return `
      <tr>
        <th class="text-left text-[1vw] border-b-[1px] border-[#FFFFFF4C] ml-[0.5vw]">${row.title}</th>
        <td class="text-left text-[1vw] border-b-[1px] border-[#FFFFFF4C] pl-[0.5vw]" >${row.data}</td>
      </tr>`;
  };

  const overlay = `
    <h1 class="font-bold text-[1.875vw] leading-[120%] mb-[1.25vw] m-[2.5vw]">DEV ENVIRONMENT</h1>
    <div class="information">
      <table>${dataRows.map(formatDataRow).join('')}</table>
    </div>
  `;

  const div = OverlayManager.createOverlayElement(
    'debugOverlay',
    'bg-black/50 font-tv2 opacity-100 flex justify-center items-center text-white flex-col absolute bottom-auto pb-[1vw] left-0 right-0 top-0 transition-opacity duration-200 enabled'
  );
  div.innerHTML = overlay;

  return div;
};
